import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Drone, droneLabel } from '../../types/drone';
import { droneService } from '../../services/droneService';
import { ApiError } from '../../types/api';

/**
 * Where a remote pilot sets this aircraft's automatic-landing threshold (#629).
 *
 * # What this component does not know
 *
 * It does not know the floor, the ceiling, or the fleet default. All three arrive
 * from the server on `drone.landingPolicy`, and the refusal arrives from the
 * server too. That is #578's rule — the browser explains, it does not decide —
 * and it matters more than usual here: the floor is provisional, expected to move
 * once there is recorded ESC current-draw data (#38), and a copy of it in this
 * file would be the fifth place one rule is written down. That duplication is the
 * whole of what #629 was filed to undo.
 *
 * It also does not know what a volt means in charge. The percentage beside the
 * input is the server's `threshold_percent`, absent for a pack whose chemistry
 * has no measured discharge curve — and absent is rendered as absent rather than
 * computed from a curve this file would then have to carry.
 *
 * # Why raising it asks first
 *
 * A threshold raised above the charge the aircraft is at right now lands it
 * immediately. That is correct and it is abrupt, so it is confirmed rather than
 * simply done.
 */
interface LandingThresholdControlProps {
  drone: Drone;
}

export const LandingThresholdControl: React.FC<LandingThresholdControlProps> = ({ drone }) => {
  const policy = drone.landingPolicy;
  const [open, setOpen] = useState(false);
  const [volts, setVolts] = useState('');
  const [dwell, setDwell] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmLandNow, setConfirmLandNow] = useState(false);

  // Without a policy the server has not told us anything, and without a described
  // pack there is no cell count to interpret volts with — so the honest surface is
  // the reason rather than an input that cannot succeed.
  if (!policy) {
    return null;
  }

  const describedPack = policy.pack !== undefined;

  const begin = () => {
    setVolts(policy.thresholdVoltsPerCell.toFixed(2));
    setDwell(String(policy.thresholdDwellSeconds));
    setError(null);
    setOpen(true);
  };

  // Would this threshold land the aircraft the moment it arrives? Only answerable
  // when the server gave us a percentage for it and the drone has reported a
  // charge; when it is not answerable, nothing is claimed.
  const wouldLandNow = (asVolts: number): boolean => {
    const reading = drone.battery.percentage;
    if (reading === null || policy.thresholdPercent === undefined) {
      return false;
    }
    // Scale the known (volts -> percent) pair to the value being typed. Crude,
    // and deliberately so: it decides whether to ask a question, never what the
    // aircraft does.
    const perVolt = policy.thresholdPercent / policy.thresholdVoltsPerCell;
    return asVolts * perVolt > reading;
  };

  const submit = async (confirmed: boolean) => {
    const asVolts = Number(volts);
    const asDwell = Number(dwell);

    if (!Number.isFinite(asVolts) || !Number.isFinite(asDwell)) {
      setError('Enter a voltage and a settling time.');
      return;
    }
    if (!confirmed && wouldLandNow(asVolts)) {
      setConfirmLandNow(true);
      return;
    }

    setConfirmLandNow(false);
    setSaving(true);
    setError(null);
    try {
      await droneService.setLandingPolicy(drone.id, {
        threshold: { volts_per_cell: asVolts, dwell_seconds: asDwell },
      });
      setOpen(false);
    } catch (err) {
      // The server's own message, not a rewrite of it. Each refusal names which
      // bound was crossed and what that bound means, and paraphrasing it here
      // would lose the reason — which is the only part a pilot can act on.
      const apiError = err as ApiError;
      setError(apiError.serverMessage ?? apiError.message ?? 'The change was refused.');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    setError(null);
    try {
      await droneService.setLandingPolicy(drone.id, { threshold: null });
      setOpen(false);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.serverMessage ?? apiError.message ?? 'The change was refused.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Button size="small" onClick={begin} disabled={!describedPack}>
        {open ? 'Editing landing threshold' : 'Change landing threshold'}
      </Button>
      {!describedPack && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Describe this drone&apos;s battery pack before setting a threshold in volts: without a
          cell count the number cannot be turned into the pack voltage the autopilot uses.
        </Typography>
      )}

      <Collapse in={open}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          <TextField
            label="Volts per cell"
            value={volts}
            onChange={(e) => setVolts(e.target.value)}
            size="small"
            type="number"
            // The bounds the server served, offered to the input. The browser
            // enforces them as a convenience and the server enforces them as the
            // rule — a value that slips past this still comes back refused.
            inputProps={{
              min: policy.floorVoltsPerCell,
              max: policy.ceilingVoltsPerCell,
              step: 0.01,
              'aria-describedby': 'landing-threshold-bounds',
            }}
          />
          <Typography id="landing-threshold-bounds" variant="caption" color="text.secondary">
            Between {policy.floorVoltsPerCell.toFixed(2)} and{' '}
            {policy.ceilingVoltsPerCell.toFixed(2)} V per cell. There is no setting that switches
            the automatic landing off.
          </Typography>

          <TextField
            label="Settling time (seconds)"
            value={dwell}
            onChange={(e) => setDwell(e.target.value)}
            size="small"
            type="number"
            inputProps={{ min: 1, step: 1, 'aria-describedby': 'landing-threshold-dwell' }}
          />
          <Typography id="landing-threshold-dwell" variant="caption" color="text.secondary">
            How long the voltage must stay below the threshold before the aircraft acts. Voltage
            sags under load and recovers, so a climb must not land the drone.
          </Typography>

          {error && (
            <Alert severity="error" role="alert">
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="contained" disabled={saving} onClick={() => submit(false)}>
              Save
            </Button>
            <Button size="small" disabled={saving || policy.isDefault} onClick={reset}>
              Use fleet default
            </Button>
            <Button size="small" disabled={saving} onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Collapse>

      <Dialog open={confirmLandNow} onClose={() => setConfirmLandNow(false)}>
        <DialogTitle>Land {droneLabel(drone)} now?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This threshold is above the charge {droneLabel(drone)} is reporting, so the aircraft will begin
            an automatic landing as soon as the change reaches it — wherever it is.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLandNow(false)}>Cancel</Button>
          <Button onClick={() => submit(true)} color="warning" variant="contained">
            Set it anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
